import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { formatDate } from '../utils/date';

// axios direto, nao o services/api: aquele injeta interceptors de sessao e
// redireciona para /login em 401. Esta pagina e publica — quem chega aqui
// veio de um QR num papel e nao tem conta no sistema.
const publico = axios.create({ baseURL: '/api' });

const SITUACAO = {
  VIGENTE: {
    rotulo: 'Documento autentico e VIGENTE',
    detalhe: 'Esta e a versao aprovada em uso. A copia confere com o original.',
    cor: 'text-status-approved',
    fundo: 'bg-status-approved/10 border-status-approved/30',
  },
  SUPERADA: {
    rotulo: 'Documento autentico, mas DESATUALIZADO',
    detalhe: 'A copia saiu do sistema, porem ja existe uma versao aprovada mais recente. '
      + 'Procure a versao vigente antes de usar este documento.',
    cor: 'text-status-pending',
    fundo: 'bg-status-pending/10 border-status-pending/30',
  },
  ARQUIVADA: {
    rotulo: 'Documento autentico, versao ARQUIVADA',
    detalhe: 'A versao foi retirada de circulacao pela politica de retencao. Nao use esta copia.',
    cor: 'text-status-pending',
    fundo: 'bg-status-pending/10 border-status-pending/30',
  },
  NAO_APROVADA: {
    rotulo: 'Copia de versao NAO APROVADA',
    detalhe: 'Esta copia saiu do sistema antes da aprovacao. Nao tem validade como documento controlado.',
    cor: 'text-status-rejected',
    fundo: 'bg-status-rejected/10 border-status-rejected/30',
  },
  REMOVIDO: {
    rotulo: 'Documento autentico, mas REMOVIDO',
    detalhe: 'A copia foi emitida por este sistema, mas o documento nao esta mais no acervo.',
    cor: 'text-ink-secondary',
    fundo: 'bg-surface-inset border-subtle',
  },
};

/**
 * SHA-256 do arquivo, calculado AQUI no navegador.
 *
 * O PDF nunca sai da maquina de quem verifica: so o hash e enviado. Como o
 * documento pode ser confidencial, subir o arquivo para um endpoint publico
 * so para comparar dois digests seria expor o conteudo a toa.
 *
 * `crypto.subtle` exige contexto seguro — funciona em HTTPS e em localhost.
 */
async function hashDoArquivo(file) {
  const buffer = await file.arrayBuffer();
  const digest = await window.crypto.subtle.digest('SHA-256', buffer);
  return [...new Uint8Array(digest)].map((b) => b.toString(16).padStart(2, '0')).join('');
}

export default function VerifyCopy() {
  const { code: codeUrl } = useParams();
  const navigate = useNavigate();
  const [codigo, setCodigo] = useState(codeUrl || '');
  const [resultado, setResultado] = useState(null);
  const [erro, setErro] = useState('');
  const [carregando, setCarregando] = useState(false);
  const [integridade, setIntegridade] = useState(null);
  const [conferindo, setConferindo] = useState(false);

  async function conferirArquivo(e) {
    const file = e.target.files?.[0];
    e.target.value = ''; // permite reenviar o mesmo arquivo
    if (!file || !resultado?.code) return;

    setConferindo(true); setIntegridade(null); setErro('');
    try {
      if (!window.crypto?.subtle) {
        throw new Error('Seu navegador nao permite calcular a impressao digital nesta conexao. '
          + 'Use HTTPS ou um navegador atualizado.');
      }
      const hash = await hashDoArquivo(file);
      const { data } = await publico.post(`/verify/${resultado.code}/hash`, { hash });
      setIntegridade({ ...data, arquivo: file.name });
    } catch (err) {
      setErro(err.message?.includes('navegador')
        ? err.message
        : err.response?.data?.error || 'Nao foi possivel conferir o arquivo.');
    }
    setConferindo(false);
  }

  const verificar = useCallback(async (valor) => {
    const alvo = (valor || '').trim();
    if (!alvo) return;
    setCarregando(true); setErro(''); setResultado(null); setIntegridade(null);
    try {
      const { data } = await publico.get(`/verify/${encodeURIComponent(alvo)}`);
      setResultado(data);
    } catch (err) {
      setErro(err.response?.status === 429
        ? 'Muitas tentativas. Aguarde um minuto.'
        : err.response?.data?.error || 'Nao foi possivel verificar agora.');
    }
    setCarregando(false);
  }, []);

  // Chegou pelo QR: ja verifica sem a pessoa clicar em nada
  useEffect(() => { if (codeUrl) verificar(codeUrl); }, [codeUrl, verificar]);

  function onSubmit(e) {
    e.preventDefault();
    navigate(`/verificar/${encodeURIComponent(codigo.trim())}`);
    verificar(codigo);
  }

  const info = resultado?.valido ? SITUACAO[resultado.situacao] : null;

  return (
    <div className="min-h-screen flex items-start justify-center px-4 py-12 bg-surface-inset">
      <div className="w-full max-w-xl">
        <div className="text-center mb-8">
          <h1 className="text-xl font-bold tracking-tight">Verificacao de documento</h1>
          <p className="text-sm text-ink-secondary mt-1">
            Informe o codigo impresso no rodape do documento para conferir a origem e a situacao.
          </p>
        </div>

        <form onSubmit={onSubmit} className="bg-surface-100 border rounded-md p-4 mb-4">
          <label className="block text-sm font-medium text-ink-secondary mb-1" htmlFor="codigo">
            Codigo de verificacao
          </label>
          <div className="flex gap-2">
            <input
              id="codigo"
              className="form-input font-mono tracking-wider"
              value={codigo}
              onChange={(e) => setCodigo(e.target.value)}
              placeholder="QD-XXXX-XXXX-XXXX"
              autoComplete="off"
              spellCheck={false}
            />
            <button className="btn btn-primary" disabled={carregando || !codigo.trim()}>
              {carregando ? 'Verificando...' : 'Verificar'}
            </button>
          </div>
        </form>

        {erro && (
          <div className="p-4 rounded-md text-sm bg-status-rejected/10 text-status-rejected border border-status-rejected/20">
            {erro}
          </div>
        )}

        {resultado?.valido && info && (
          <div className={`border rounded-md p-4 ${info.fundo}`}>
            <div className={`text-base font-semibold ${info.cor}`}>{info.rotulo}</div>
            <p className="text-sm text-ink-secondary mt-1">{info.detalhe}</p>

            <dl className="mt-4 border-t border-subtle pt-3">
              {[
                ['Documento', resultado.documento.codigo],
                ['Nome', resultado.documento.nome],
                // Copia tirada da versao de trabalho: o numero que circula
                // oficialmente e o da versao aprovada que ela gerou. Mostrar
                // so "001.3" faria a pessoa procurar no sistema e nao achar.
                ['Versao', resultado.versao_publicada
                  ? `${resultado.versao_publicada} (emitida como ${resultado.versao})`
                  : resultado.versao],
                ['Aprovada em', resultado.aprovada_em ? formatDate(resultado.aprovada_em) : '—'],
                ['Copia emitida em', formatDate(resultado.emitida_em)],
                ['Codigo', resultado.code],
              ].map(([rotulo, valor]) => (
                <div key={rotulo} className="flex justify-between gap-3 py-1.5 border-b border-subtle last:border-0">
                  <dt className="text-xs text-ink-muted flex-shrink-0">{rotulo}</dt>
                  <dd className="text-sm font-medium text-right break-words">{valor}</dd>
                </div>
              ))}
            </dl>

            <p className="text-2xs text-ink-muted mt-4">
              Documento controlado emitido pelo sistema de gestao da qualidade da Quality
              Engineering. Esta pagina confirma origem e situacao; ela nao exibe o conteudo
              do documento.
            </p>
          </div>
        )}

        {/* Integridade: so faz sentido depois de o codigo ser reconhecido */}
        {resultado?.valido && (
          <div className="bg-surface-100 border rounded-md p-4 mt-4">
            <h2 className="text-sm font-semibold">Conferir o arquivo (opcional)</h2>
            <p className="text-xs text-ink-secondary mt-1">
              O passo acima confirma que o codigo saiu daqui. Este confere se o
              <strong> conteudo do arquivo</strong> continua exatamente como foi emitido.
            </p>

            <label className="btn btn-secondary btn-sm mt-3 inline-block cursor-pointer">
              {conferindo ? 'Conferindo...' : 'Selecionar o PDF'}
              <input type="file" accept="application/pdf,.pdf" className="hidden"
                onChange={conferirArquivo} disabled={conferindo} />
            </label>

            <p className="text-2xs text-ink-muted mt-2">
              O arquivo <strong>nao e enviado</strong>. A impressao digital e calculada no seu
              proprio navegador e so ela viaja ate nos.
            </p>

            {integridade && !integridade.conferivel && (
              <div className="mt-3 p-3 rounded border bg-surface-inset border-subtle">
                <div className="text-sm font-semibold text-ink-secondary">Nao verificavel</div>
                <p className="text-xs text-ink-secondary mt-1">{integridade.motivo}</p>
              </div>
            )}

            {integridade?.conferivel && integridade.integro === true && (
              <div className="mt-3 p-3 rounded border bg-status-approved/10 border-status-approved/30">
                <div className="text-sm font-semibold text-status-approved">
                  Arquivo integro
                </div>
                <p className="text-xs text-ink-secondary mt-1">
                  <span className="font-mono">{integridade.arquivo}</span> confere byte a byte
                  com o PDF emitido por este sistema. O conteudo nao foi alterado.
                </p>
              </div>
            )}

            {integridade?.conferivel && integridade.integro === false && (
              <div className="mt-3 p-3 rounded border bg-status-rejected/10 border-status-rejected/30">
                <div className="text-sm font-semibold text-status-rejected">
                  Arquivo diferente do emitido
                </div>
                <p className="text-xs text-ink-secondary mt-1">
                  <span className="font-mono">{integridade.arquivo}</span> nao confere com o PDF
                  original desta copia.
                </p>
                {/* Nao afirmamos adulteracao: reabrir e salvar num leitor de PDF
                    reescreve o arquivo sem mudar o conteudo visivel, e isso
                    tambem muda o hash. Dizer "foi adulterado" acusaria alguem
                    por um falso positivo comum. */}
                <p className="text-xs text-ink-secondary mt-2">
                  Isso acontece se o conteudo foi alterado, mas tambem se o PDF foi reaberto e
                  salvo de novo por algum programa, o que reescreve o arquivo sem mudar o que se
                  ve. Em caso de duvida, peca uma copia nova pelo sistema.
                </p>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
